/**
 * Agents v3 — the KPI-targets line on an agent's config page: every scheduled
 * agent compares results with the brand's KPI targets (read through the run
 * context), so the page says whether any are set and links to where they are
 * edited (`/client-reports/targets`).
 *
 * The KPI endpoints are `portal.kpis.manage`-gated, so the note renders only
 * for people who can read them; a load failure renders nothing rather than a
 * wrong claim.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCan } from '../../../lib/rbac';
import { listKpiTargets } from '../../../lib/api/client-reports/kpi-targets-api';
import '../../client-reports/client-reports.scss';

interface Props {
  slug: string;
  brandName: string;
  agentLabel: string;
}

const KpiTargetsNote = ({ slug, brandName, agentLabel }: Props) => {
  const { t } = useTranslation();
  const canKpis = useCan('portal.kpis.manage');
  const [count, setCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCount(null);
    setFailed(false);
    if (!canKpis || !slug) return undefined;
    listKpiTargets(slug)
      .then((rows) => {
        if (!cancelled) setCount(Array.isArray(rows) ? rows.length : 0);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, canKpis]);

  if (!canKpis || failed || count === null) return null;

  const brand = brandName || t('AgentThisBrand', 'this brand');

  if (count === 0) {
    return (
      <div
        className='client-agents__note client-agents__note--warn'
        data-testid='kpi-targets-note'
      >
        <span className='client-agents__note-title'>
          {t('AgentKpiNoneTitle', 'No KPI targets set for {{brand}} yet', {
            brand,
          })}
        </span>
        <span className='client-agents__note-body'>
          {t(
            'AgentKpiNoneBody',
            "{{agent}} still runs, but its output can't say whether results are on target until you set them.",
            { agent: agentLabel }
          )}
        </span>
        <Link className='client-agents__open-link' to='/client-reports/targets'>
          {t('AgentKpiSet', 'Set KPI targets →')}
        </Link>
      </div>
    );
  }

  return (
    <div className='client-agents__note' data-testid='kpi-targets-note'>
      <span className='client-agents__note-body'>
        {count === 1
          ? t(
              'AgentKpiComparesOne',
              'Compares every result with the {{count}} KPI target set for {{brand}}.',
              { count, brand }
            )
          : t(
              'AgentKpiComparesMany',
              'Compares every result with the {{count}} KPI targets set for {{brand}}.',
              { count, brand }
            )}
      </span>
      <Link className='client-agents__open-link' to='/client-reports/targets'>
        {t('AgentKpiEdit', 'Edit KPI targets →')}
      </Link>
    </div>
  );
};

export default KpiTargetsNote;
